/**
 * How far a spoken word is from the word on the page, counted in reading
 * errors rather than in letters.
 *
 * The recogniser hands back letters with no vowels, spelled the way its
 * training data spells that word: modern words in full spelling (חוכמה,
 * לאכול), biblical words the way they appear in quoted scripture (לבך,
 * השמים), and no consistency between the two. The corpus is spelled by
 * rule (Academy full spelling: ליבך, השמיים, וייטע). Comparing the two as
 * strings therefore charges the child for the recogniser's habits, and the
 * bigram score that used to do the comparing was worse still: on a three
 * letter word it could only answer 0, 0.5 or 1.
 *
 * So both words are reduced to a skeleton first, and then compared by edit
 * distance where each edit costs what it is worth as a reading error:
 *
 *   - a word whose letters are all said exactly like the page is correct
 *     (ע for א, ט for ת, a silent letter at the end of a word, and ק for כּ,
 *     ח for כ, ס for שׂ, ו for ב where the dots on the page agree): nobody can
 *     hear the difference, the recogniser included. Only when that is the whole
 *     difference; next to a real error the close letter costs half;
 *   - a near sound or an accent costs half an error (ל for an American ר, ס
 *     for an Ashkenazi ת, or a dotted pair where the page says otherwise):
 *     still a read word, shown amber;
 *   - any other substitution, insertion or deletion costs one.
 *
 * The verdict is then a count a teacher can read: 0 is correct, up to 1 is
 * a near miss, more is wrong.
 */

const HEBREW_LETTER = /[\u05D0-\u05EA]/;
const NOT_LETTER = /[^\u05D0-\u05EA]/g;

const FINAL_TO_REGULAR: Record<string, string> = {
  'ך': 'כ',
  'ם': 'מ',
  'ן': 'נ',
  'ף': 'פ',
  'ץ': 'צ',
};

/**
 * Letters an Israeli reader says the same way. Nobody can hear the
 * difference, the recogniser included.
 */
const SAME_SOUND: ReadonlyArray<[string, string]> = [
  ['א', 'ע'],
  ['ת', 'ט'],
];

/**
 * Letters that sound alike only with the right dot on the page: כּ is ק, כ is
 * ח, שׂ is ס, ב is ו. The written side decides, and only when we can see the
 * dots. The recogniser's own letter (heard כ, ש, ו) can stand for either
 * sound, so a heard כ against a written ק or ח is the same sound too.
 */
const DOTTED: ReadonlyArray<{ written: string; heard: string; same: (m: LetterMarks) => boolean }> = [
  { written: 'כ', heard: 'ק', same: (m) => m.dagesh },
  { written: 'כ', heard: 'ח', same: (m) => !m.dagesh },
  { written: 'ש', heard: 'ס', same: (m) => m.sinDot },
  { written: 'ב', heard: 'ו', same: (m) => !m.dagesh },
];

/** [written, heard]: the recogniser's letter covers both sounds. */
const HEARD_COVERS: ReadonlyArray<[string, string]> = [
  ['ק', 'כ'],
  ['ח', 'כ'],
  ['ס', 'ש'],
  ['ו', 'ב'],
];

/**
 * Letter pairs that are close but not always the same, priced at half an
 * error when they come with another difference in the word. These are pairs,
 * not classes: כ sounds like ק with a dagesh and like ח without one, but ק
 * never sounds like ח. The last two are accents: an American reish reaches
 * the recogniser as ל (המעליב for המעריב), an Ashkenazi tav as ס (בריס).
 */
const HOMOPHONES: ReadonlyArray<[string, string]> = [
  ['א', 'ע'],
  ['כ', 'ק'],
  ['כ', 'ח'],
  ['ת', 'ט'],
  ['ס', 'ש'],
  ['ב', 'ו'],
  ['ר', 'ל'],
  ['ת', 'ס'],
];

/** Silent at the end of a word, so interchangeable there and only there. */
const SILENT_FINALS = new Set(['א', 'ה', 'ע']);

export const HOMOPHONE_COST = 0.5;

/** The dots on one letter of the printed word, where we have them. */
export interface LetterMarks {
  dagesh: boolean;
  sinDot: boolean;
}

const either = (pairs: ReadonlyArray<[string, string]>, a: string, b: string) =>
  pairs.some(([x, y]) => (a === x && b === y) || (a === y && b === x));

/** Is `heard` said exactly like `written` here? */
function soundsSame(heard: string, written: string, atEnd: boolean, marks?: LetterMarks): boolean {
  if (heard === written) return true;
  if (atEnd && SILENT_FINALS.has(heard) && SILENT_FINALS.has(written)) return true;
  if (either(SAME_SOUND, heard, written)) return true;
  if (HEARD_COVERS.some(([w, h]) => written === w && heard === h)) return true;
  const dotted = DOTTED.find((d) => d.written === written && d.heard === heard);
  return Boolean(dotted && marks && dotted.same(marks));
}

/** Half an error for a close letter, one for any other. */
function substitutionCost(heard: string, written: string, atEnd: boolean): number {
  if (heard === written) return 0;
  if (atEnd && SILENT_FINALS.has(heard) && SILENT_FINALS.has(written)) return HOMOPHONE_COST;
  return either(HOMOPHONES, heard, written) ? HOMOPHONE_COST : 1;
}

const DAGESH = 'ּ';
const SIN_DOT = 'ׂ';

/**
 * The dots on each letter of a pointed word, in step with skeleton(): the same
 * letters dropped, so marks[i] belongs to skeleton(word)[i].
 */
export function skeletonMarks(pointed: string): LetterMarks[] {
  const letters: { letter: string; marks: LetterMarks }[] = [];
  for (const ch of pointed) {
    if (HEBREW_LETTER.test(ch)) letters.push({ letter: ch, marks: { dagesh: false, sinDot: false } });
    else if (letters.length && ch === DAGESH) letters[letters.length - 1].marks.dagesh = true;
    else if (letters.length && ch === SIN_DOT) letters[letters.length - 1].marks.sinDot = true;
  }
  if (letters.length <= 2) return letters.map((l) => l.marks);
  return letters
    .filter((l, i) => !(i > 0 && i < letters.length - 1 && (l.letter === 'ו' || l.letter === 'י')))
    .map((l) => l.marks);
}

/**
 * The skeleton of a word: consonants only, finals unified, vowel letters
 * inside the word dropped.
 *
 * Dropping every inner vav and yod is deliberately blunt. It makes ליבך and
 * לבך the same word, and השמיים, השמים and שמים the same word, which is what
 * the reader deserves. It also makes a few pairs of genuinely different
 * words the same (a word with a consonantal vav and one without), and that
 * is accepted: the aligner only ever compares a spoken word with the next
 * few words of the text, not with a dictionary.
 */
export function skeleton(word: string): string {
  const letters = word.replace(NOT_LETTER, '');
  if (letters.length <= 2) return letters.replace(/[ךםןףץ]/g, (c) => FINAL_TO_REGULAR[c]);

  const out: string[] = [];
  for (let i = 0; i < letters.length; i++) {
    const c = letters[i];
    const inner = i > 0 && i < letters.length - 1;
    if (inner && (c === 'ו' || c === 'י')) continue;
    out.push(FINAL_TO_REGULAR[c] ?? c);
  }
  return out.join('');
}

/**
 * Weighted edit distance between two skeletons, `a` heard and `b` written.
 *
 * A word whose letters all sound exactly like the page is the word: 0, green
 * (Elie, 2 Oct 2026). Only when that is the whole difference: a sound-alike
 * letter next to a real error is priced as before, half plus one, so a real
 * error is not let through. Measured on 27 marked readings: 33 more correct
 * words green, the same real errors red.
 *
 * Otherwise standard Levenshtein, with substitution priced by
 * substitutionCost. `bMarks` are the dots on b's letters (skeletonMarks).
 */
export function weightedDistance(a: string, b: string, bMarks?: LetterMarks[]): number {
  if (a === b) return 0;
  const n = a.length;
  const m = b.length;
  if (n === m && [...a].every((c, i) => soundsSame(c, b[i], i === n - 1, bMarks?.[i]))) return 0;
  if (n === 0) return m;
  if (m === 0) return n;

  let prev = new Array<number>(m + 1);
  let cur = new Array<number>(m + 1);
  for (let j = 0; j <= m; j++) prev[j] = j;

  for (let i = 1; i <= n; i++) {
    cur[0] = i;
    for (let j = 1; j <= m; j++) {
      const atEnd = i === n && j === m;
      const sub = substitutionCost(a[i - 1], b[j - 1], atEnd);
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + sub);
    }
    [prev, cur] = [cur, prev];
  }
  return prev[m];
}

export interface WordMatch {
  /** Reading errors between the spoken word and the closest accepted form. */
  edits: number;
  /** 1 for identical skeletons, falling towards 0; used to rank candidates. */
  similarity: number;
}

/**
 * The best match of a spoken word against a written word and its accepted
 * readings (the Divine Name is written one way and said several).
 *
 * `pointed` is the written word as printed, with its nikud, when the text
 * being scored lost it (the full-spelling copy). Its dots decide the
 * sound-alike letters, and only for candidates[0], the written word itself.
 */
export function matchWord(spoken: string, candidates: string[], pointed?: string): WordMatch {
  const s = skeleton(spoken);
  let best: WordMatch = { edits: Infinity, similarity: 0 };
  for (const [k, candidate] of candidates.entries()) {
    const c = skeleton(candidate);
    if (!c) continue;
    const source = k === 0 ? pointed ?? candidate : undefined;
    // Use the dots only when the printed word has the same letters as this one.
    const marks = source && skeleton(source) === c ? skeletonMarks(source) : undefined;
    const edits = weightedDistance(s, c, marks);
    const similarity = Math.max(0, 1 - edits / Math.max(s.length, c.length, 1));
    if (edits < best.edits) best = { edits, similarity };
  }
  return best;
}

export type WordVerdict = 'correct' | 'near' | 'wrong';

/** 0 errors is correct, up to one is a near miss, more is wrong. */
export function verdictFor(edits: number): WordVerdict {
  if (edits === 0) return 'correct';
  if (edits <= 1) return 'near';
  return 'wrong';
}

/**
 * The most edits a spoken word may be from a text word and still be taken as
 * an attempt at it. Two errors on a two-letter word is a different word.
 */
export function maxEditsForMatch(written: string): number {
  return skeleton(written).length <= 3 ? 1 : 2;
}

export function isHebrewWord(word: string): boolean {
  return HEBREW_LETTER.test(word);
}
